/**
 * L'OUTIL DE RECADRAGE DU DEPOT B2B.
 *
 * ⛔ POURQUOI IL EXISTE. Adrien, 14/08/2026 (serie 210) : « a l'importer il doit y avoir
 * l'outil de recadrage comme dans le studio, sinon ca ne sera pas accepte par l'atelier ».
 * Jusqu'ici le depot B2B envoyait le fichier BRUT : une image 16/9 partait telle quelle vers
 * un papier 50 x 70, et personne ne decidait de ce qui serait coupe. Ni le client, qui ne
 * voyait rien, ni l'atelier, qui recevait un fichier au mauvais rapport et devait choisir a
 * la place du client, ou refuser. Le recadrage n'est donc pas un confort : c'est ce qui rend
 * le fichier imprimable.
 *
 * ⛔ ON EXPORTE CE QUE LE CLIENT A VU. Le cadre est fixe, l'image bouge dessous, et l'export
 * decoupe exactement le rectangle affiche, aux pixels de la SOURCE (aucun reechantillonnage a
 * la taille de l'ecran : la definition mesuree ensuite serait celle de l'apercu, pas celle du
 * tirage). Meme regle que la composition personnalisee : ce qui part a l'impression est ce
 * qui a ete valide, pas un recalcul.
 *
 * ⛔ LE PAYSAGE EST LE MEME RAPPORT, RETOURNE. Adrien : « on doit pouvoir importer des formats
 * paysage au meme ratio que le format portrait ». Le rapport est l'inverse, pas un rapport
 * libre. L'orientation par defaut est celle de l'image deposee, pour que le geste courant ne
 * demande aucun clic.
 *
 * ⛔ ET CE RAPPORT EST CELUI DE L'IMPRESSION, PAS CELUI DU PAPIER (14/08/2026). L'appelant le
 * donne : toute la boutique compose au 0,6923 de la zone imprimee, marges comprises, et le
 * B2B suit le meme contrat que la page personnalisee. Les centimetres du format ne servent
 * plus qu'a dire la DEFINITION reelle du tirage.
 *
 * ⛔ UN PDF NE SE RECADRE PAS ICI. Le navigateur ne sait pas le rasteriser sans bibliotheque.
 * On le laisse passer et on le DIT, plutot que d'afficher un outil qui ne ferait rien.
 *
 * Interface publique, appelee par `b2b.js` :
 *   B2BRecadrage.ouvrir(fichier, { cm: [21,30], libelle: '21 × 30', orientation: 'auto',
 *                                  rapport: 0.6923 })
 *     -> Promise<{ blob, nom, type, px:{w,h}, orientation } | null>   (null = annule)
 *   B2BRecadrage.disponible(fichier) -> bool
 */
window.B2BRecadrage = (function () {
  "use strict";

  var TYPES_IMAGE = { "image/jpeg": 1, "image/png": 1 };

  /* ⛔ LES LIBELLES VIENNENT DE LA LOCALE, PAS DU CODE (15/08/2026). Sept phrases francaises
     etaient ecrites en dur ici : « Recadrer votre visuel », « Valider le recadrage »,
     « Portrait », « Paysage »... Un client allemand les lisait en francais, et aucun audit de
     traduction ne pouvait les voir, puisqu'un fichier .js n'est pas une ressource traduisible.
     Meme cause que la page B2B a moitie francaise, meme jour. Le dictionnaire est celui du
     theme, pose par layout/theme.liquid. */
  function tr(cle, repli) {
    var D = window.MURMURE_I18N || {};
    return D[cle] || repli;
  }
  /* Le JPEG sort a 0,95 : au-dela le poids double sans gain visible au traceur, en deca les
     aplats du print marquent. Le PNG reste PNG, un aplat de charte ne se recompresse pas. */
  var QUALITE_JPEG = 0.95;

  /* ⛔ L'ECHELLE DE QUALITE VIT ICI, ET NULLE PART AILLEURS (14/08/2026). Elle etait ecrite
     DEUX FOIS, mot pour mot, dans ce fichier et dans b2b.js : la fenetre de recadrage et la
     pastille de la ligne pouvaient donc juger le meme fichier differemment des qu'un seuil
     bougeait d'un cote. `b2b.js` lit celle-ci, ou ne peint aucun niveau.
     ⛔ LES SEUILS TIENNENT COMPTE DE L'AGRANDISSEMENT (Adrien, 14/08/2026 : « sois plus
     tolerant sur la qualite de l'image importee en B2B, on upscale derriere »). L'atelier
     passe chaque visuel dans un agrandisseur professionnel avant le tirage, jusqu'a six fois
     la definition d'origine. Le plancher n'est donc pas « la definition qui imprime bien »,
     c'est « la definition A PARTIR DE LAQUELLE l'agrandissement atteint 150 ppp », le seuil
     du grand format : 150 / 6 = 25. En dessous, l'agrandisseur invente plus qu'il ne
     restitue, et le refus est honnete.
     ⚠️ 240 ppp NE TENAIT PAS DEBOUT sur une affiche regardee a un metre : c'est un seuil de
     magazine tenu a trente centimetres. Il peignait en « moyen » des fichiers que l'atelier
     imprime tres bien, et en « mauvais » des fichiers qu'il imprime bien apres agrandissement.
     Nouvelle echelle : 150 = rien a faire, 60 = l'agrandissement finit le travail, 25 = il
     porte tout le tirage, en dessous on refuse. */
  var PPP_PLANCHER = 25;

  function qualite(ppp) {
    if (!(ppp > 0)) return { niveau: "inconnu", bloquant: false };
    if (ppp >= 150) return { niveau: "tresbon", bloquant: false };
    if (ppp >= 60) return { niveau: "bon", bloquant: false };
    if (ppp >= PPP_PLANCHER) return { niveau: "moyen", bloquant: false };
    return { niveau: "mauvais", bloquant: true };
  }

  function disponible(fichier) {
    return !!(fichier && TYPES_IMAGE[fichier.type]);
  }

  function lireImage(fichier) {
    return new Promise(function (res, rej) {
      var url = URL.createObjectURL(fichier);
      var img = new Image();
      img.onload = function () { res({ img: img, url: url }); };
      img.onerror = function () { URL.revokeObjectURL(url); rej(new Error("image illisible")); };
      img.src = url;
    });
  }

  function el(tag, cls, txt) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (txt != null) n.textContent = txt;
    return n;
  }

  /**
   * @param {File} fichier
   * @param {{cm:number[], libelle:string, orientation:string}} opts
   * @returns {Promise<Object|null>}
   */
  function ouvrir(fichier, opts) {
    opts = opts || {};
    var cm = opts.cm || [21, 30];
    var court = Math.min(cm[0], cm[1]);
    var longp = Math.max(cm[0], cm[1]);

    return lireImage(fichier).then(function (src) {
      return new Promise(function (resoudre) {
        var img = src.img;
        var natW = img.naturalWidth || img.width;
        var natH = img.naturalHeight || img.height;

        /* L'orientation de depart est celle de l'image : le cas courant ne coute aucun clic.
           Une image carree part en portrait, comme le catalogue. */
        var orientation = opts.orientation === "portrait" || opts.orientation === "paysage"
          ? opts.orientation
          : (natW > natH ? "paysage" : "portrait");

        /* ⛔ LE RAPPORT DE COUPE VIENT DE L'APPELANT, PAS DES CENTIMETRES (14/08/2026). Il
           valait `court / longp`, c'est-a-dire le rapport du PAPIER : la boutique, elle,
           imprime au 0,6923 de la zone imprimee, quel que soit le format. Les centimetres
           restent, mais pour la DEFINITION seule (des pouces reels sous les pixels retenus).
           Sans consigne, on retombe sur le papier : un appelant qui ne sait pas ce qu'il veut
           obtient au moins un cadre coherent avec le format qu'il annonce. */
        var rapportPortrait = Number(opts.rapport) > 0
          ? Number(opts.rapport)
          : (court / longp);

        function rapport() {
          return orientation === "paysage" ? (1 / rapportPortrait) : rapportPortrait;
        }

        /* --- Ossature --- */
        var fond = el("div", "b2bcrop");
        var boite = el("div", "b2bcrop__box");
        var barre = el("div", "b2bcrop__bar");
        barre.appendChild(el("h2", null, tr("b2bcrop_titre", "Recadrer votre visuel")));
        var fermer = el("button", "b2bcrop__x", "×");
        fermer.type = "button";
        fermer.setAttribute("aria-label", tr("b2bcrop_fermer", "Fermer"));
        barre.appendChild(fermer);

        var scene = el("div", "b2bcrop__scene");
        var cadre = el("div", "b2bcrop__cadre");
        var toile = el("canvas", "b2bcrop__toile");
        var ctx = toile.getContext("2d");
        cadre.appendChild(toile);
        cadre.appendChild(el("i", "b2bcrop__grille"));
        scene.appendChild(cadre);

        var outils = el("div", "b2bcrop__outils");
        var sens = el("div", "b2bcrop__sens sans");
        var bPortrait = el("button", "b2bcrop__sens-b", tr("b2bcrop_portrait", "Portrait"));
        var bPaysage = el("button", "b2bcrop__sens-b", tr("b2bcrop_paysage", "Paysage"));
        bPortrait.type = bPaysage.type = "button";
        sens.appendChild(bPortrait);
        sens.appendChild(bPaysage);
        var zoom = el("input", "b2bcrop__zoom");
        zoom.type = "range";
        zoom.min = "1"; zoom.max = "4"; zoom.step = "0.01"; zoom.value = "1";
        zoom.setAttribute("aria-label", tr("b2bcrop_zoom", "Zoom"));
        outils.appendChild(sens);
        outils.appendChild(zoom);

        var info = el("p", "b2bcrop__info sans");
        var pied = el("div", "b2bcrop__pied");
        var bAnnuler = el("button", "b2bcrop__annuler sans", tr("b2bcrop_annuler", "Annuler"));
        var bValider = el("button", "b2bcrop__valider sans", tr("b2bcrop_valider", "Valider le recadrage"));
        bAnnuler.type = bValider.type = "button";
        pied.appendChild(bAnnuler);
        pied.appendChild(bValider);

        boite.appendChild(barre);
        boite.appendChild(scene);
        boite.appendChild(outils);
        boite.appendChild(info);
        boite.appendChild(pied);
        fond.appendChild(boite);
        document.body.appendChild(fond);
        document.documentElement.classList.add("b2bcrop-ouvert");

        /* --- Etat de la vue ---
           `ech` est l'echelle appliquee a l'image DANS le cadre, 1 = l'image couvre tout
           juste le cadre (comme `object-fit: cover`). `cx,cy` sont les centres relatifs de
           la zone visible, en fraction de l'image : 0,5 / 0,5 = centre. Deux nombres, pas
           une matrice : c'est ce qui rend l'export lisible plus bas. */
        var vue = { ech: 1, cx: 0.5, cy: 0.5 };

        function tailleCadre() {
          var maxL = Math.min(scene.clientWidth || 520, 640);
          var maxH = scene.clientHeight || 420;
          var r = rapport();
          var w = maxL, h = w / r;
          if (h > maxH) { h = maxH; w = h * r; }
          return { w: Math.round(w), h: Math.round(h) };
        }

        /* La zone de la SOURCE effectivement retenue, en pixels image. C'est la seule
           fonction qui traduit la vue en coordonnees source : l'apercu et l'export
           l'appellent tous les deux, donc ils ne peuvent pas diverger. */
        function zoneSource() {
          var r = rapport();
          var rImg = natW / natH;
          var sw, sh;
          if (rImg > r) { sh = natH; sw = sh * r; }   /* image plus large : on rogne a gauche/droite */
          else { sw = natW; sh = sw / r; }            /* image plus haute : on rogne en haut/bas */
          sw /= vue.ech; sh /= vue.ech;
          var sx = vue.cx * natW - sw / 2;
          var sy = vue.cy * natH - sh / 2;
          sx = Math.max(0, Math.min(natW - sw, sx));
          sy = Math.max(0, Math.min(natH - sh, sy));
          return { sx: sx, sy: sy, sw: sw, sh: sh };
        }

        function peindre() {
          var t = tailleCadre();
          cadre.style.width = t.w + "px";
          cadre.style.height = t.h + "px";
          var dpr = Math.min(window.devicePixelRatio || 1, 2);
          toile.width = Math.round(t.w * dpr);
          toile.height = Math.round(t.h * dpr);
          var z = zoneSource();
          ctx.clearRect(0, 0, toile.width, toile.height);
          ctx.drawImage(img, z.sx, z.sy, z.sw, z.sh, 0, 0, toile.width, toile.height);

          /* La definition rendue au format choisi, calculee sur la zone RETENUE : c'est le
             chiffre qui compte, pas celui du fichier d'origine. Un 6000 px recadre au quart
             n'imprime plus qu'avec 1500. */
          var cmLong = longp;
          var pxLong = Math.max(z.sw, z.sh);
          var ppp = Math.round(pxLong / (cmLong / 2.54));
          var q = qualite(ppp);
          info.className = "b2bcrop__info sans b2bcrop__info--" + q.niveau;
          /* ⛔ NE JAMAIS CONSEILLER UN GESTE IMPOSSIBLE. Le message disait « dézoomez ou
             changez de fichier » MEME quand le curseur de zoom touchait deja sa butee gauche :
             Adrien l'a releve le 14/08/2026 sur une capture ou il ne restait rien a dezoomer.
             Un conseil qu'on ne peut pas suivre se lit comme une panne, et il fait chercher le
             defaut du cote de l'outil au lieu du fichier. On ne propose donc le dezoom que
             s'il reste du zoom a rendre. */
          var suite = "";
          if (q.bloquant) {
            suite = vue.ech > 1.01
              ? ". Trop petit pour ce format : dézoomez, ou déposez un fichier plus grand."
              : ". Ce fichier est trop petit pour ce format, même après agrandissement : déposez-en un plus grand.";
          }
          /* ⛔ LA PROMESSE EN GRAS, LE CONSTAT EN MAIGRE (Adrien, serie 227). Le client lit
             cette ligne au moment ou son fichier vient d'etre juge « moyen » : ce qu'il doit
             retenir n'est pas le nombre de ppp, c'est que nous nous en occupons. Un texte
             uniforme laisse la reserve au premier plan et la reponse au second.
             ⚠️ EN DEUX NOEUDS, PAS EN innerHTML : cette ligne contient des dimensions calculees
             et un libelle de format qui vient d'un reglage. Y injecter du HTML ouvrirait une
             porte pour un gain de zero. */
          info.textContent = tr("b2bcrop_zone", "Zone retenue : __W__ × __H__ px, soit __P__ ppp sur un __F__")
            .replace("__W__", Math.round(z.sw)).replace("__H__", Math.round(z.sh))
            .replace("__P__", ppp).replace("__F__", opts.libelle || (court + " × " + longp))
            + (orientation === "paysage" ? tr("b2bcrop_couche", " couché") : "")
            + suite;
          if (!q.bloquant && q.niveau === "moyen") {
            var fort = document.createElement("b");
            fort.textContent = tr("b2bcrop_agrandi", " Nous l'agrandissons en haute définition avant le tirage.");
            info.appendChild(fort);
          }
          bValider.disabled = q.bloquant;
          bPortrait.setAttribute("aria-pressed", String(orientation === "portrait"));
          bPaysage.setAttribute("aria-pressed", String(orientation === "paysage"));
          bPortrait.classList.toggle("est-actif", orientation === "portrait");
          bPaysage.classList.toggle("est-actif", orientation === "paysage");
        }

        /* --- Deplacement : la souris et le doigt passent par le meme chemin --- */
        var glisse = null;
        function debut(x, y) { glisse = { x: x, y: y, cx: vue.cx, cy: vue.cy }; }
        function bouge(x, y) {
          if (!glisse) return;
          var t = tailleCadre();
          var z = zoneSource();
          /* Un pixel d'ecran vaut `sw / largeurCadre` pixels source : le deplacement suit le
             doigt exactement, quel que soit le zoom. */
          vue.cx = glisse.cx - (x - glisse.x) * (z.sw / t.w) / natW;
          vue.cy = glisse.cy - (y - glisse.y) * (z.sh / t.h) / natH;
          peindre();
        }
        function fin() { glisse = null; }

        cadre.addEventListener("pointerdown", function (e) {
          cadre.setPointerCapture(e.pointerId); debut(e.clientX, e.clientY);
        });
        cadre.addEventListener("pointermove", function (e) { if (glisse) { e.preventDefault(); bouge(e.clientX, e.clientY); } });
        cadre.addEventListener("pointerup", fin);
        cadre.addEventListener("pointercancel", fin);
        cadre.addEventListener("wheel", function (e) {
          e.preventDefault();
          vue.ech = Math.max(1, Math.min(4, vue.ech * (e.deltaY < 0 ? 1.08 : 1 / 1.08)));
          zoom.value = String(vue.ech);
          peindre();
        }, { passive: false });

        zoom.addEventListener("input", function () { vue.ech = Number(zoom.value) || 1; peindre(); });

        function basculer(o) {
          if (orientation === o) return;
          orientation = o;
          vue.ech = 1; vue.cx = 0.5; vue.cy = 0.5;
          zoom.value = "1";
          peindre();
        }
        bPortrait.addEventListener("click", function () { basculer("portrait"); });
        bPaysage.addEventListener("click", function () { basculer("paysage"); });

        var surResize = function () { peindre(); };
        window.addEventListener("resize", surResize);

        function refermer(resultat) {
          window.removeEventListener("resize", surResize);
          document.removeEventListener("keydown", surTouche);
          document.documentElement.classList.remove("b2bcrop-ouvert");
          fond.remove();
          URL.revokeObjectURL(src.url);
          resoudre(resultat);
        }
        function surTouche(e) { if (e.key === "Escape") refermer(null); }
        document.addEventListener("keydown", surTouche);

        fermer.addEventListener("click", function () { refermer(null); });
        bAnnuler.addEventListener("click", function () { refermer(null); });
        fond.addEventListener("click", function (e) { if (e.target === fond) refermer(null); });

        bValider.addEventListener("click", function () {
          var z = zoneSource();
          /* ⛔ L'EXPORT EST A LA DEFINITION DE LA SOURCE, PAS A CELLE DU CADRE. Le canvas
             fait la taille de la zone retenue en pixels image : un dessin a la taille de
             l'apercu diviserait la definition par cinq sans que rien ne le dise, et le
             controle de ppp d'apres coup lirait le chiffre de l'apercu. */
          var out = document.createElement("canvas");
          out.width = Math.max(1, Math.round(z.sw));
          out.height = Math.max(1, Math.round(z.sh));
          var octx = out.getContext("2d");
          octx.imageSmoothingQuality = "high";
          /* Le PNG garde sa transparence ; le JPEG n'en a pas, on pose du blanc plutot que
             de laisser le noir par defaut du canvas apparaitre sur un papier blanc. */
          if (fichier.type !== "image/png") {
            octx.fillStyle = "#fff";
            octx.fillRect(0, 0, out.width, out.height);
          }
          octx.drawImage(img, z.sx, z.sy, z.sw, z.sh, 0, 0, out.width, out.height);
          out.toBlob(function (blob) {
            if (!blob) { refermer(null); return; }
            var base = (fichier.name || "visuel").replace(/\.[^.]+$/, "");
            var ext = fichier.type === "image/png" ? ".png" : ".jpg";
            refermer({
              blob: blob,
              nom: base + "-recadre" + ext,
              type: fichier.type === "image/png" ? "image/png" : "image/jpeg",
              px: { w: out.width, h: out.height },
              orientation: orientation
            });
          }, fichier.type === "image/png" ? "image/png" : "image/jpeg", QUALITE_JPEG);
        });

        peindre();
      });
    }).catch(function () { return null; });
  }

  /* `qualite` sort d'ici parce que la pastille des lignes doit juger comme la fenetre. */
  return { ouvrir: ouvrir, disponible: disponible, qualite: qualite };
})();
